import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Wrench, Users, Layers, Percent } from "lucide-react";
import TablaDrivers from "./TablaDrivers";
import MargenBrutoProyeccion from "./MargenBrutoProyeccion";

// R-10: la sección de gastos de explotación se separa en vistas por grupo de
// cuenta del PGC. Cada sub-pestaña reutiliza TablaDrivers filtrando por el
// prefijo del código de línea (retrocompatible: sin filtroCodigos se verían
// todas las líneas).
//
// COBERTURA COMPLETA (fix del descuadre drivers ↔ PyG proyectado, 2026-08-19):
// las partidas del PyG agregan MÁS cuentas que los grupos 60/62/64 —
// "4. Aprovisionamientos" incluye 610-612 (variación de existencias) y 693/793
// (deterioros), y "7. Otros gastos de explotación" incluye 631/634/636/639
// (tributos), 650/651/659 (otras pérdidas de gestión) y 694/695/794
// (provisiones comerciales). Antes esas líneas proyectaban por fallback SIN
// aparecer en ninguna sub-pestaña y el total visible no casaba con el PyG.
// La pestaña "Resto explotación" las hace visibles y driveables. La 68x se
// gestiona en CAPEX y la 66x en Financiación (a propósito: un solo sitio de
// edición por concepto).
const GRUPOS_GASTO = [
  {
    id: "grupo-60",
    label: "Compras (60)",
    icon: Package,
    // C2 (Héctor 29-09): la variación de existencias (61x) se muestra AQUÍ,
    // junto a las compras — contablemente ambas forman "Aprovisionamientos".
    // Antes vivía en "Resto explotación" y descolocaba.
    filtroCodigos: ["60", "61"],
    titulo: "Driver de Compras / Aprovisionamientos (grupos 60 y 61)",
    descripcion:
      "Aquí se proyectan las compras a proveedores (60x) y la variación de " +
      "existencias (61x) — juntas forman casi toda la partida \"4. " +
      "Aprovisionamientos\" del PyG; solo faltan los deterioros de mercaderías " +
      "(693/793), que están en \"Resto explotación\". Si usas la NOF en días, " +
      "la variación de existencias del balance la determina la NOF (pon la 610 " +
      "a 0 para no duplicar, como hace el plan de EDSER).",
  },
  {
    id: "grupo-62",
    label: "Servicios exteriores (62)",
    icon: Wrench,
    filtroCodigos: "62",
    titulo: "Driver de Servicios exteriores (grupo 62)",
    descripcion:
      "Proyección de servicios exteriores (cuentas 62x). Ojo: la partida \"Otros gastos de " +
      "explotación\" del PyG proyectado suma además tributos, otras pérdidas de gestión y " +
      "provisiones (pestaña Resto explotación) y la cuota de renting (Financiación).",
  },
  {
    id: "grupo-64",
    label: "Gastos de personal (64)",
    icon: Users,
    filtroCodigos: "64",
    titulo: "Driver de Gastos de personal (grupo 64)",
    descripcion: "Proyección de gastos de personal (cuentas 64x)",
  },
  {
    id: "grupo-resto",
    label: "Resto explotación",
    icon: Layers,
    // Cuentas de las partidas 4 y 7 del PyG que NO son 60x/62x/64x: sin esta
    // pestaña proyectan igualmente (fallback plano/IPC) pero eran invisibles
    // y el total de las otras pestañas no casaba con el PyG proyectado.
    filtroCodigos: ["631", "634", "636", "639", "65", "693", "694", "695", "793", "794"],
    titulo: "Resto de gastos de explotación (fuera de 60/61/62/64)",
    descripcion:
      "Tributos (631/634/636/639), otras pérdidas de gestión (65x), " +
      "provisiones y deterioros comerciales (693/694/695/793/794). Estas cuentas entran en " +
      "\"Aprovisionamientos\" y \"Otros gastos de explotación\" del PyG proyectado: sin driver " +
      "proyectan constantes (o al IPC). La variación de existencias (61x) está en Compras, " +
      "la amortización (68x) en CAPEX y los gastos financieros (66x) en Financiación.",
  },
];

export default function DriverPygGastosContent({ empresaId, ejercicios, aniosProyectar, readOnly }) {
  return (
    <Tabs defaultValue="grupo-60" className="w-full space-y-4">
      <TabsList className="flex flex-wrap gap-1">
        {GRUPOS_GASTO.map((g) => {
          const Icon = g.icon;
          return (
            <TabsTrigger key={g.id} value={g.id} className="gap-2">
              <Icon className="h-4 w-4" />
              <span className="text-sm">{g.label}</span>
            </TabsTrigger>
          );
        })}
        {/* C1 (Héctor 29-09): margen bruto histórico + proyectado */}
        <TabsTrigger value="margen" className="gap-2" data-testid="tab-margen-proy">
          <Percent className="h-4 w-4" />
          <span className="text-sm">Margen Bruto</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="margen" className="mt-0">
        <MargenBrutoProyeccion empresaId={empresaId} />
      </TabsContent>

      {GRUPOS_GASTO.map((g) => (
        <TabsContent key={g.id} value={g.id} className="mt-0">
          <TablaDrivers
            empresaId={empresaId}
            ejercicios={ejercicios}
            seccionPyg="gastos"
            titulo={g.titulo}
            descripcion={g.descripcion}
            aniosProyectar={aniosProyectar}
            invertirSemantica
            readOnly={readOnly}
            filtroCodigos={g.filtroCodigos}
            storageKey={`finreport:nivel:${empresaId}:gastos-${g.id}`}
            nivelesPermitidos={["3D", "MAX"]}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}
